import type { ConnectorSide } from "./canvas-object";

// コネクタ（接続矢印）の幾何計算。図形と図形を直交折れ線で結ぶための純粋関数群。
// クライアント描画（connector-object.tsx / オーバーレイ）と PDF / サムネイル出力に加え、
// サーバ側の参照整合（保存 bbox の再計算）でも共用するため domain に置く。

export interface ConnectorFrame {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /**
   * 図形の回転角（度・時計回り）。未指定は 0（非回転）。
   * アンカー位置・外向き方向・外接矩形を、図形中心まわりに回転させて算出するために使う。
   */
  readonly rotation?: number | undefined;
}

export interface ConnectorPoint {
  readonly x: number;
  readonly y: number;
}

// 端点から外向きに伸ばす助走（スタブ）の長さ。矢印が図形へ垂直に出入りするように見せる。
export const CONNECTOR_STUB_PX = 24;

// 迂回ルート（同方向・背中合わせ・直交干渉）で図形の外側に確保するクリアランス（px）。
const DETOUR_MARGIN_PX = CONNECTOR_STUB_PX;

// コネクタ保存 bbox に与える余白（px）。矢じり・線幅の張り出しを含め、かつ水平/垂直コネクタで
// w/h が 0 になって範囲選択・他参加者選択枠が潰れるのを防ぐ。描画は端点から都度計算するため、
// この保存 bbox は AABB を参照する箇所（範囲選択・ミニマップ・他参加者選択枠・カリング）専用。
export const CONNECTOR_BBOX_PAD_PX = 12;

// 端点が同一座標に潰れたときに連続点を間引くための許容誤差。
const EPSILON = 0.01;

/** 点を中心 (cx, cy) まわりに deg 度（時計回り・画面 y 下向き座標系）回転する。 */
function rotatePoint(p: ConnectorPoint, cx: number, cy: number, deg: number): ConnectorPoint {
  if (!deg) return p;
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - cx;
  const dy = p.y - cy;
  // y 下向き座標系では [[cos,-sin],[sin,cos]] が見た目の時計回り回転になる（CSS rotate と一致）。
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

/** フレーム中心（ステージ座標）。回転の原点に使う。 */
function frameCenter(frame: ConnectorFrame): ConnectorPoint {
  return { x: frame.x + frame.w / 2, y: frame.y + frame.h / 2 };
}

/** ステージ座標の点を、図形の局所（未回転）座標へ変換する（中心まわりに -rotation 回転）。 */
function toLocalPoint(p: ConnectorPoint, frame: ConnectorFrame): ConnectorPoint {
  const c = frameCenter(frame);
  return rotatePoint(p, c.x, c.y, -(frame.rotation ?? 0));
}

/**
 * オブジェクト外接矩形の指定辺の中点（接続アンカー）をステージ座標で返す。
 * frame.rotation があれば、図形中心まわりに回転させた実際の辺中点を返す。
 */
export function sideAnchor(frame: ConnectorFrame, side: ConnectorSide): ConnectorPoint {
  const base = baseSideAnchor(frame, side);
  if (!frame.rotation) return base;
  const c = frameCenter(frame);
  return rotatePoint(base, c.x, c.y, frame.rotation);
}

/** 非回転前提の辺中点。 */
function baseSideAnchor(frame: ConnectorFrame, side: ConnectorSide): ConnectorPoint {
  switch (side) {
    case "top":
      return { x: frame.x + frame.w / 2, y: frame.y };
    case "bottom":
      return { x: frame.x + frame.w / 2, y: frame.y + frame.h };
    case "left":
      return { x: frame.x, y: frame.y + frame.h / 2 };
    case "right":
      return { x: frame.x + frame.w, y: frame.y + frame.h / 2 };
  }
}

/**
 * 指定辺の外向き単位ベクトル（非回転）を返す（top は上向き= y- 方向）。
 */
export function sideDirection(side: ConnectorSide): ConnectorPoint {
  switch (side) {
    case "top":
      return { x: 0, y: -1 };
    case "bottom":
      return { x: 0, y: 1 };
    case "left":
      return { x: -1, y: 0 };
    case "right":
      return { x: 1, y: 0 };
  }
}

/**
 * 回転を考慮した外向き単位ベクトルを返す。接続ドットの外向きオフセットや矢印アイコンの
 * 向き決定に使う（UI 表示用。orthogonal ルーティングには axis 吸着した {@link exitAxis} を使う）。
 */
export function sideUnitVector(side: ConnectorSide, rotationDeg = 0): ConnectorPoint {
  const base = sideDirection(side);
  if (!rotationDeg) return base;
  return rotatePoint(base, 0, 0, rotationDeg);
}

/**
 * 回転後の外向き方向を 4 軸（上下左右）のうち最も近い軸へ吸着した単位ベクトルを返す。
 * 直交折れ線ルーティングは軸平行のスタブ／中継線を前提とするため、回転図形でも軸へ丸めて
 * 経路の直交性を保つ（非回転では恒等で従来挙動と一致する）。
 */
function exitAxis(side: ConnectorSide, rotationDeg = 0): ConnectorPoint {
  const v = sideUnitVector(side, rotationDeg);
  return Math.abs(v.x) >= Math.abs(v.y)
    ? { x: v.x >= 0 ? 1 : -1, y: 0 }
    : { x: 0, y: v.y >= 0 ? 1 : -1 };
}

/**
 * フレームの軸平行外接矩形（AABB）を返す。回転がある場合は 4 隅を回転させた包含矩形を返す。
 * 迂回ルートで図形を避けるための障害物矩形として使う。
 */
export function frameAABB(frame: ConnectorFrame): ConnectorFrame {
  if (!frame.rotation) return { x: frame.x, y: frame.y, w: frame.w, h: frame.h };
  const c = frameCenter(frame);
  const corners = [
    { x: frame.x, y: frame.y },
    { x: frame.x + frame.w, y: frame.y },
    { x: frame.x + frame.w, y: frame.y + frame.h },
    { x: frame.x, y: frame.y + frame.h },
  ].map((p) => rotatePoint(p, c.x, c.y, frame.rotation as number));
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
}

/**
 * 指定点に最も近い辺を返す。接続先のスナップ辺の決定に使う。
 * 4 辺のアンカーのうち、点との距離が最小の辺を選ぶ（回転を考慮した実アンカー位置で比較する）。
 */
export function nearestSide(frame: ConnectorFrame, point: ConnectorPoint): ConnectorSide {
  const sides: readonly ConnectorSide[] = ["top", "right", "bottom", "left"];
  let best: ConnectorSide = "top";
  let bestDist = Infinity;
  for (const side of sides) {
    const anchor = sideAnchor(frame, side);
    const dist = (anchor.x - point.x) ** 2 + (anchor.y - point.y) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = side;
    }
  }
  return best;
}

/**
 * 端点 p から外向きへ伸ばすスタブ端点を返す。
 *
 * - 進む向きは辺の実際の外向き法線 `normal`（回転込み）を使う。回転図形でも辺へ垂直に出るため、
 *   軸吸着方向だと回転矩形の角を貫通してしまう不具合を避けられる（非回転では軸方向と一致）。
 * - スタブ長は、接続相手 `other` が外向き軸 `axis` 方向にある（向かい合う）場合のみ相手との距離の
 *   半分を上限に抑える。図形間隔が短い（< 2 * STUB）ときにスタブが相手側へ突き抜けて経路が図形内部を
 *   通るのを防ぐ。相手が逆方向（背を向け合う / 重なり）にあるときは迂回のため規定長を確保する。
 */
function stubEnd(
  p: ConnectorPoint,
  normal: ConnectorPoint,
  axis: ConnectorPoint,
  other: ConnectorPoint
): ConnectorPoint {
  // 相手アンカーの、外向き軸に沿う距離（along）と直交方向のずれ（perp）。
  const along = (other.x - p.x) * axis.x + (other.y - p.y) * axis.y;
  const perpX = other.x - p.x - along * axis.x;
  const perpY = other.y - p.y - along * axis.y;
  const perp = Math.hypot(perpX, perpY);
  // スタブを縮めるのは「相手が前方かつ近接し、ほぼ正対している（横ずれが小さい）」場合のみ。
  // 横ずれが大きい配置（回転でアンカーが軸方向にほぼ一直線になる等）でクランプすると、スタブ長が
  // ほぼ 0 になって dedupe で消え、端点近傍の線が図形を貫通する。その場合は規定長を確保する。
  const clamp = along > 0 && along < 2 * CONNECTOR_STUB_PX && perp < CONNECTOR_STUB_PX;
  const len = clamp ? Math.min(CONNECTOR_STUB_PX, along / 2) : CONNECTOR_STUB_PX;
  return { x: p.x + normal.x * len, y: p.y + normal.y * len };
}

const right = (f: ConnectorFrame): number => f.x + f.w;
const bottom = (f: ConnectorFrame): number => f.y + f.h;

/** 矩形を四方へ margin だけ広げる。 */
function inflate(box: ConnectorFrame, margin: number): ConnectorFrame {
  return { x: box.x - margin, y: box.y - margin, w: box.w + margin * 2, h: box.h + margin * 2 };
}

/**
 * スタブ端 point から、外向き軸 axis 方向へ膨張 AABB box の外側まで逃がした点を返す。
 * 回転図形では辺法線方向へ STUB 出しても膨張 AABB の内側に留まることがあり、その点をそのまま
 * グリッド経路探索の始点/終点にすると経路を作れない。axis 方向の遠い辺まで押し出して探索を開始
 * できるようにする。既に外側にある場合は point をそのまま返す。
 */
function escapeFromInflatedBox(
  point: ConnectorPoint,
  axis: ConnectorPoint,
  box: ConnectorFrame
): ConnectorPoint {
  if (axis.x > 0) return { x: Math.max(point.x, right(box)), y: point.y };
  if (axis.x < 0) return { x: Math.min(point.x, box.x), y: point.y };
  if (axis.y > 0) return { x: point.x, y: Math.max(point.y, bottom(box)) };
  return { x: point.x, y: Math.min(point.y, box.y) };
}

/** 軸平行セグメントが矩形の内部（端から eps だけ内側）を通過するか。 */
function segHitsInterior(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  box: ConnectorFrame,
  eps = 0.01
): boolean {
  const ix0 = box.x + eps;
  const ix1 = box.x + box.w - eps;
  const iy0 = box.y + eps;
  const iy1 = box.y + box.h - eps;
  if (ix1 <= ix0 || iy1 <= iy0) return false;
  if (Math.abs(x0 - x1) < eps) {
    // 縦セグメント（x 一定）。
    if (x0 <= ix0 || x0 >= ix1) return false;
    const lo = Math.min(y0, y1);
    const hi = Math.max(y0, y1);
    return hi > iy0 && lo < iy1;
  }
  // 横セグメント（y 一定）。
  if (y0 <= iy0 || y0 >= iy1) return false;
  const lo = Math.min(x0, x1);
  const hi = Math.max(x0, x1);
  return hi > ix0 && lo < ix1;
}

/** 線分が（回転を考慮した）図形フレームの内部を通過するか。Liang-Barsky を局所座標で行う。 */
function segHitsFrameInterior(
  p: ConnectorPoint,
  q: ConnectorPoint,
  frame: ConnectorFrame,
  eps = 0.01
): boolean {
  // 図形の局所（未回転）座標へ変換してから軸平行矩形との交差を解く。
  const a = frame.rotation ? toLocalPoint(p, frame) : p;
  const b = frame.rotation ? toLocalPoint(q, frame) : q;
  const x0 = frame.x + eps;
  const x1 = frame.x + frame.w - eps;
  const y0 = frame.y + eps;
  const y1 = frame.y + frame.h - eps;
  if (x1 <= x0 || y1 <= y0) return false;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let t0 = 0;
  let t1 = 1;
  // Liang-Barsky。p:勾配係数, qv:境界までの余裕。
  const clip = (pp: number, qv: number): boolean => {
    if (Math.abs(pp) < 1e-12) return qv >= 0;
    const r = qv / pp;
    if (pp < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };
  if (!clip(-dx, a.x - x0)) return false;
  if (!clip(dx, x1 - a.x)) return false;
  if (!clip(-dy, a.y - y0)) return false;
  if (!clip(dy, y1 - a.y)) return false;
  return t1 - t0 > 1e-9;
}

/** ルート（点列）のいずれかのセグメントが 2 つの図形フレーム（回転考慮）いずれかの内部を貫通するか。 */
function routeHitsFrames(
  route: readonly ConnectorPoint[],
  frameA: ConnectorFrame,
  frameB: ConnectorFrame
): boolean {
  for (let i = 0; i < route.length - 1; i++) {
    const p = route[i]!;
    const q = route[i + 1]!;
    if (segHitsFrameInterior(p, q, frameA)) return true;
    if (segHitsFrameInterior(p, q, frameB)) return true;
  }
  return false;
}

/** 値の昇順ユニーク配列（微小誤差を丸めて重複を畳む）。 */
function uniqSortedNums(values: readonly number[]): number[] {
  const rounded = values.map((v) => Math.round(v * 1000) / 1000);
  return [...new Set(rounded)].sort((a, b) => a - b);
}

function nearestIndex(values: readonly number[], v: number): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < values.length; i++) {
    const d = Math.abs(values[i]! - v);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/**
 * a1 → b1 を、2 つの障害物矩形（既に余白を含めて膨張済み）の内部を貫通しない直交折れ線で結ぶ
 * 中間点列を返す。図形の辺・a1・b1 が作る格子上で、曲がり数→長さの順に最小の経路を Dijkstra で探索する。
 * 経路が見つからない場合は null（呼び出し側は簡易ルートにフォールバックする）。
 *
 * 単純な迂回（同方向 / 直交など）が相手図形を横切ってしまう配置で、確実に外側へ回り込むための保険。
 */
function gridDetourMidpoints(
  a1: ConnectorPoint,
  b1: ConnectorPoint,
  boxA: ConnectorFrame,
  boxB: ConnectorFrame
): ConnectorPoint[] | null {
  const xs = uniqSortedNums([a1.x, b1.x, boxA.x, right(boxA), boxB.x, right(boxB)]);
  const ys = uniqSortedNums([a1.y, b1.y, boxA.y, bottom(boxA), boxB.y, bottom(boxB)]);
  const nx = xs.length;
  const ny = ys.length;
  const nodeCount = nx * ny;
  const nodeId = (ix: number, iy: number): number => ix * ny + iy;
  const startIx = nearestIndex(xs, a1.x);
  const startIy = nearestIndex(ys, a1.y);
  const goalIx = nearestIndex(xs, b1.x);
  const goalIy = nearestIndex(ys, b1.y);

  const free = (x0: number, y0: number, x1: number, y1: number): boolean =>
    !segHitsInterior(x0, y0, x1, y1, boxA) && !segHitsInterior(x0, y0, x1, y1, boxB);

  // 状態 = ノード × 進入方向（0:なし, 1:水平, 2:垂直）。曲がり数を最小化するため方向も状態に含める。
  const DIRS = 3;
  const BEND_COST = 1e6;
  const stateCount = nodeCount * DIRS;
  const dist = new Array<number>(stateCount).fill(Infinity);
  const prev = new Array<number>(stateCount).fill(-1);
  const visited = new Array<boolean>(stateCount).fill(false);
  const startState = (startIx * ny + startIy) * DIRS + 0;
  dist[startState] = 0;

  const popMin = (): number => {
    let best = -1;
    let bestD = Infinity;
    for (let s = 0; s < stateCount; s++) {
      if (!visited[s] && dist[s]! < bestD) {
        bestD = dist[s]!;
        best = s;
      }
    }
    return best;
  };

  while (true) {
    const s = popMin();
    if (s < 0) break;
    visited[s] = true;
    const dir = s % DIRS;
    const node = (s - dir) / DIRS;
    const iy = node % ny;
    const ix = (node - iy) / ny;
    // 4 近傍（格子上の隣接座標）へ展開する。
    const neighbors: ReadonlyArray<readonly [number, number, number]> = [
      [ix - 1, iy, 1],
      [ix + 1, iy, 1],
      [ix, iy - 1, 2],
      [ix, iy + 1, 2],
    ];
    for (const [jx, jy, moveDir] of neighbors) {
      if (jx < 0 || jx >= nx || jy < 0 || jy >= ny) continue;
      const x0 = xs[ix]!;
      const y0 = ys[iy]!;
      const x1 = xs[jx]!;
      const y1 = ys[jy]!;
      if (!free(x0, y0, x1, y1)) continue;
      const length = Math.abs(x1 - x0) + Math.abs(y1 - y0);
      const bend = dir !== 0 && dir !== moveDir ? 1 : 0;
      const nextState = nodeId(jx, jy) * DIRS + moveDir;
      const nd = dist[s]! + length + bend * BEND_COST;
      if (nd < dist[nextState]!) {
        dist[nextState] = nd;
        prev[nextState] = s;
      }
    }
  }

  // ゴールノードの 3 方向のうち最小コストの状態から経路を復元する。
  let goalState = -1;
  let goalBest = Infinity;
  for (let d = 0; d < DIRS; d++) {
    const st = nodeId(goalIx, goalIy) * DIRS + d;
    if (dist[st]! < goalBest) {
      goalBest = dist[st]!;
      goalState = st;
    }
  }
  if (goalState < 0 || goalBest === Infinity) return null;

  const points: ConnectorPoint[] = [];
  for (let s = goalState; s >= 0; s = prev[s]!) {
    const dir = s % DIRS;
    const node = (s - dir) / DIRS;
    const iy = node % ny;
    const ix = (node - iy) / ny;
    points.push({ x: xs[ix]!, y: ys[iy]! });
    if (s === startState) break;
  }
  points.reverse();
  // 先頭（a1）・末尾（b1）を除いた中間点のみを返す。
  return dedupePoints(points).slice(1, -1);
}

/**
 * 2 つの図形を避けつつ a1 → b1 を直交折れ線で結ぶ中間点を返す。
 * 端点の外向き軸 dir1 / dir2 の組み合わせ（同方向 / 向かい合い / 背中合わせ / 直交）に応じて、
 * 図形内部を貫通したり経路が往復・重複したりしないよう、必要なら図形の外側へ迂回する。
 */
function orthogonalMidpoints(
  a1: ConnectorPoint,
  dir1: ConnectorPoint,
  b1: ConnectorPoint,
  dir2: ConnectorPoint,
  aBox: ConnectorFrame,
  bBox: ConnectorFrame,
  a: ConnectorPoint,
  b: ConnectorPoint
): ConnectorPoint[] {
  const horiz1 = dir1.x !== 0;
  const horiz2 = dir2.x !== 0;

  if (horiz1 && horiz2) {
    // 両端とも水平。
    if (dir1.x === dir2.x) {
      // 同じ向き（共に右 / 共に左）: 両図形の外側に縦チャネルを置いて回り込む。
      const channelX =
        dir1.x > 0
          ? Math.max(a1.x, b1.x, right(aBox) + DETOUR_MARGIN_PX, right(bBox) + DETOUR_MARGIN_PX)
          : Math.min(a1.x, b1.x, aBox.x - DETOUR_MARGIN_PX, bBox.x - DETOUR_MARGIN_PX);
      return [
        { x: channelX, y: a1.y },
        { x: channelX, y: b1.y },
      ];
    }
    // 逆向き。
    if ((b.x - a.x) * dir1.x > 0) {
      // 向かい合い: 中央の縦線で結ぶ（Z 字）。
      const midX = (a1.x + b1.x) / 2;
      return [
        { x: midX, y: a1.y },
        { x: midX, y: b1.y },
      ];
    }
    // 背中合わせ（外向きが互いに逆を向く）: 両図形の外側（左右の外端）と、上下の外側チャネルで
    // 矩形状に回り込む。立ち上がり（縦線）を a1.x / b1.x ではなく外端コラムに置くことで、
    // 相手図形と縦方向にずれていても図形内部を横切らない。
    const leftX = Math.min(aBox.x, bBox.x) - DETOUR_MARGIN_PX;
    const rightX = Math.max(right(aBox), right(bBox)) + DETOUR_MARGIN_PX;
    const fromX = dir1.x < 0 ? leftX : rightX;
    const toX = dir2.x < 0 ? leftX : rightX;
    const channelY = chooseAroundY(aBox, bBox, a, b);
    return [
      { x: fromX, y: a1.y },
      { x: fromX, y: channelY },
      { x: toX, y: channelY },
      { x: toX, y: b1.y },
    ];
  }

  if (!horiz1 && !horiz2) {
    // 両端とも垂直。
    if (dir1.y === dir2.y) {
      // 同じ向き（共に下 / 共に上）: 両図形の外側に横チャネルを置いて回り込む。
      const channelY =
        dir1.y > 0
          ? Math.max(a1.y, b1.y, bottom(aBox) + DETOUR_MARGIN_PX, bottom(bBox) + DETOUR_MARGIN_PX)
          : Math.min(a1.y, b1.y, aBox.y - DETOUR_MARGIN_PX, bBox.y - DETOUR_MARGIN_PX);
      return [
        { x: a1.x, y: channelY },
        { x: b1.x, y: channelY },
      ];
    }
    if ((b.y - a.y) * dir1.y > 0) {
      // 向かい合い: 中央の横線で結ぶ（Z 字）。
      const midY = (a1.y + b1.y) / 2;
      return [
        { x: a1.x, y: midY },
        { x: b1.x, y: midY },
      ];
    }
    // 背中合わせ: 両図形の外側（上下の外端）と、左右の外側チャネルで矩形状に回り込む。
    // 立ち上がり（横線）を a1.y / b1.y ではなく外端ロウに置くことで、相手図形と横方向にずれていても
    // 図形内部を横切らない。
    const topY = Math.min(aBox.y, bBox.y) - DETOUR_MARGIN_PX;
    const botY = Math.max(bottom(aBox), bottom(bBox)) + DETOUR_MARGIN_PX;
    const fromY = dir1.y < 0 ? topY : botY;
    const toY = dir2.y < 0 ? topY : botY;
    const channelX = chooseAroundX(aBox, bBox, a, b);
    return [
      { x: a1.x, y: fromY },
      { x: channelX, y: fromY },
      { x: channelX, y: toY },
      { x: b1.x, y: toY },
    ];
  }

  // 片方が水平・片方が垂直（直交）。
  if (horiz1) {
    // from が水平・to が垂直。単一の角は (b1.x, a1.y)。
    const cornerCleanFrom = dir1.x > 0 ? b1.x >= right(aBox) : b1.x <= aBox.x;
    const cornerCleanTo = dir2.y > 0 ? a1.y >= bottom(bBox) : a1.y <= bBox.y;
    if (cornerCleanFrom && cornerCleanTo) {
      return [{ x: b1.x, y: a1.y }];
    }
    // 単一の角だと図形を横切る / 往復する → to の外側に横チャネルを取って迂回する。
    const channelY =
      dir2.y > 0
        ? Math.max(b1.y, bottom(aBox) + DETOUR_MARGIN_PX, bottom(bBox) + DETOUR_MARGIN_PX)
        : Math.min(b1.y, aBox.y - DETOUR_MARGIN_PX, bBox.y - DETOUR_MARGIN_PX);
    return [
      { x: a1.x, y: channelY },
      { x: b1.x, y: channelY },
    ];
  }
  // from が垂直・to が水平。単一の角は (a1.x, b1.y)。
  const cornerCleanFrom = dir1.y > 0 ? b1.y >= bottom(aBox) : b1.y <= aBox.y;
  const cornerCleanTo = dir2.x > 0 ? a1.x >= right(bBox) : a1.x <= bBox.x;
  if (cornerCleanFrom && cornerCleanTo) {
    return [{ x: a1.x, y: b1.y }];
  }
  const channelX =
    dir2.x > 0
      ? Math.max(b1.x, right(aBox) + DETOUR_MARGIN_PX, right(bBox) + DETOUR_MARGIN_PX)
      : Math.min(b1.x, aBox.x - DETOUR_MARGIN_PX, bBox.x - DETOUR_MARGIN_PX);
  return [
    { x: channelX, y: a1.y },
    { x: channelX, y: b1.y },
  ];
}

/** 背中合わせ（水平）時に回り込む横チャネル Y を、上下のうち近い側で選ぶ。 */
function chooseAroundY(
  aBox: ConnectorFrame,
  bBox: ConnectorFrame,
  a: ConnectorPoint,
  b: ConnectorPoint
): number {
  const aboveY = Math.min(aBox.y, bBox.y) - DETOUR_MARGIN_PX;
  const belowY = Math.max(bottom(aBox), bottom(bBox)) + DETOUR_MARGIN_PX;
  const mid = (a.y + b.y) / 2;
  return Math.abs(aboveY - mid) <= Math.abs(belowY - mid) ? aboveY : belowY;
}

/** 背中合わせ（垂直）時に回り込む縦チャネル X を、左右のうち近い側で選ぶ。 */
function chooseAroundX(
  aBox: ConnectorFrame,
  bBox: ConnectorFrame,
  a: ConnectorPoint,
  b: ConnectorPoint
): number {
  const leftX = Math.min(aBox.x, bBox.x) - DETOUR_MARGIN_PX;
  const rightX = Math.max(right(aBox), right(bBox)) + DETOUR_MARGIN_PX;
  const mid = (a.x + b.x) / 2;
  return Math.abs(leftX - mid) <= Math.abs(rightX - mid) ? leftX : rightX;
}

/**
 * 2 つの図形フレームの指定辺どうしを直交折れ線で結ぶルート点列（ステージ座標）を返す。
 * 先頭が from アンカー、末尾が to アンカー。連続する重複点は除去する。
 * 回転がある場合はアンカーを回転後の実位置に、外向き方向を最寄り軸へ吸着して算出する。
 */
export function computeConnectorRoute(
  fromFrame: ConnectorFrame,
  fromSide: ConnectorSide,
  toFrame: ConnectorFrame,
  toSide: ConnectorSide
): ConnectorPoint[] {
  const a = sideAnchor(fromFrame, fromSide);
  const b = sideAnchor(toFrame, toSide);
  // 外向き法線（回転込み）はスタブの向きに、最寄り軸へ吸着した方向は直交ルーティングの判定に使う。
  const aNormal = sideUnitVector(fromSide, fromFrame.rotation);
  const bNormal = sideUnitVector(toSide, toFrame.rotation);
  const da = exitAxis(fromSide, fromFrame.rotation);
  const db = exitAxis(toSide, toFrame.rotation);
  const aBox = frameAABB(fromFrame);
  const bBox = frameAABB(toFrame);
  // スタブは辺へ垂直に出し（回転図形でも角を貫通しない）、相手方向では距離の半分を上限に抑える
  // （近接時に図形を貫通しないように）。
  const a1 = stubEnd(a, aNormal, da, b);
  const b1 = stubEnd(b, bNormal, db, a);
  const mids = orthogonalMidpoints(a1, da, b1, db, aBox, bBox, a, b);
  const route = dedupePoints([a, a1, ...mids, b1, b]);

  // 簡易ルートが図形内部を横切る配置（同方向・直交で相手図形に重なる等）では、図形外接矩形＋余白を
  // 障害物とするグリッド経路探索で確実に外側へ迂回させる。グリッドでは規定長（クランプなし）のスタブ端
  // を用いる。近接時のクランプ済みスタブだと a1 / b1 が膨張矩形の内部に留まり、経路探索を開始できない
  // ため。スタブ区間（a→a1 / b1→b）は辺へ垂直で図形を貫通しない。経路が得られない場合は簡易ルートのまま返す。
  // 横切り判定は AABB ではなく実際の図形（回転考慮）を用いる。回転図形では端点付近の垂直スタブや
  // 迂回のヘッド/テールが AABB を横切っても実図形は横切らないことがあり、AABB 基準だと不要に簡易
  // ルートへ戻してしまうため。
  if (routeHitsFrames(route, fromFrame, toFrame)) {
    const a1Full = { x: a.x + aNormal.x * CONNECTOR_STUB_PX, y: a.y + aNormal.y * CONNECTOR_STUB_PX };
    const b1Full = { x: b.x + bNormal.x * CONNECTOR_STUB_PX, y: b.y + bNormal.y * CONNECTOR_STUB_PX };
    const inflatedA = inflate(aBox, DETOUR_MARGIN_PX);
    const inflatedB = inflate(bBox, DETOUR_MARGIN_PX);
    // 回転図形では、辺法線方向へ STUB 出した点が膨張 AABB の外に出るとは限らない。グリッド探索の
    // 始点/終点が障害物内部にあると経路を作れないため、外向き軸方向へ膨張 AABB の外側まで逃がす。
    const aGridStart = escapeFromInflatedBox(a1Full, da, inflatedA);
    const bGridGoal = escapeFromInflatedBox(b1Full, db, inflatedB);
    const gridMids = gridDetourMidpoints(aGridStart, bGridGoal, inflatedA, inflatedB);
    if (gridMids) {
      const gridRoute = dedupePoints([
        a,
        a1Full,
        aGridStart,
        ...gridMids,
        bGridGoal,
        b1Full,
        b,
      ]);
      if (!routeHitsFrames(gridRoute, fromFrame, toFrame)) return gridRoute;
    }
  }
  return route;
}

/**
 * 連続する（ほぼ）同一座標の点を 1 つに畳む。
 */
export function dedupePoints(points: readonly ConnectorPoint[]): ConnectorPoint[] {
  const result: ConnectorPoint[] = [];
  for (const point of points) {
    const prev = result[result.length - 1];
    if (prev && Math.abs(prev.x - point.x) < EPSILON && Math.abs(prev.y - point.y) < EPSILON) {
      continue;
    }
    result.push(point);
  }
  return result;
}

/**
 * 点列の外接矩形を返す。描画キャンバスのサイズ算出や保存 bbox の素として使う。
 */
export function routeBoundingBox(points: readonly ConnectorPoint[]): ConnectorFrame {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * コネクタの保存用フレーム（x/y/w/h）を返す。ルート外接矩形に矢じり・線幅ぶんの余白を加える。
 * 作成時・端点移動時の保存値を一元化し、AABB 参照箇所で見た目とずれにくくする。
 */
export function connectorStoredFrame(
  fromFrame: ConnectorFrame,
  fromSide: ConnectorSide,
  toFrame: ConnectorFrame,
  toSide: ConnectorSide
): ConnectorFrame {
  const bbox = routeBoundingBox(computeConnectorRoute(fromFrame, fromSide, toFrame, toSide));
  return {
    x: bbox.x - CONNECTOR_BBOX_PAD_PX,
    y: bbox.y - CONNECTOR_BBOX_PAD_PX,
    w: bbox.w + CONNECTOR_BBOX_PAD_PX * 2,
    h: bbox.h + CONNECTOR_BBOX_PAD_PX * 2,
  };
}
